/**
 * Copy a message — the Threads pane's button, now shared with the skill editor's chat
 * (owner).
 *
 * It lived inside `ThreadsPane` and the editor's transcript had no copy control at all, which
 * was the only difference between two transcripts that otherwise render through the same
 * `Markdown`, `ReasoningSection` and `AgentSteps`. Rather than a second implementation that
 * drifts, the one implementation moved here.
 *
 * The reference's handler is far more involved — it stages `text/html` through `execCommand` so
 * mention chips survive a paste, because Chromium's async clipboard sanitiser strips the class
 * its parser keys off. Since item BI the composer *has* chips, so this does the same: the text
 * flavour is the humanised `@label`, and when the message carries a marker the HTML flavour is
 * the `span.revit-mention` form the editor's parser rebuilds a chip from. A transcript with no
 * mentions in it takes the same path and pays nothing for it.
 *
 * Everything else follows `bimgo-ai-web`'s `Message.tsx`: lucide's `Copy` and `Check` at 16px in
 * a 28px hit area, and a green tick for two seconds. The title stays "Copy message" throughout —
 * the tick is the confirmation, and a tooltip that rewrites itself under a cursor already
 * sitting on it is a second one nobody asked for.
 *
 * The copy itself goes through `lib/clipboard`, which does **not** use
 * `navigator.clipboard.writeText` as its primary path. This shell denies every browser
 * permission but one, so Chromium refuses that call and it silently copies nothing — the
 * failure this button shipped with for three revisions.
 */
import { useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { copyText } from "../../lib/clipboard";
import { buildMentionClipboardHtml, humanizeMentionMarkers } from "../../lib/mentionUtils";
import { hasMentionMarkers } from "./MentionRenderer";

/** How long the tick stands in for a toast. `Message.tsx`'s two seconds. */
export const COPY_FEEDBACK_MS = 2000;

export function CopyButton({ text }: { text: string }) {
  const { t } = useLocale();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="msg-copy"
      title={t("chat.copyMessage")}
      aria-label={t("chat.copyMessage")}
      onClick={() => {
        // The tick only appears when the text really landed — see `lib/clipboard`
        // for why `navigator.clipboard.writeText` alone does nothing in this shell.
        void copyText(humanizeMentionMarkers(text), undefined, hasMentionMarkers(text) ? buildMentionClipboardHtml(text) : undefined).then((ok) => {
          if (!ok) return;
          setCopied(true);
          setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
        });
      }}
    >
      {copied ? (
        // lucide `Check`, the reference's icon and geometry.
        <svg className="msg-copy-icon is-copied" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      ) : (
        // lucide `Copy`.
        <svg className="msg-copy-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
        </svg>
      )}
    </button>
  );
}
